import moment from 'moment';
import React, { ReactNode } from 'react';
import { useQuery } from 'react-query';
import { ContextType } from 'api/conversations';
import { DocumentsAPI } from 'api/document/document-core';
import { DocumentContainer } from 'api/document/types/document';
import { ProjectGlobalConversation } from 'api/project-conversations';
import { SpaceTab, SpaceTabAPI } from 'api/space';
import { StatusUpdate, StatusUpdatesAPI } from 'api/status-updates';
import { Task, TaskAPI } from 'api/tasks';
import { HumanisedDate } from 'components/atoms/humanised-date';
import { useTranslate } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { useUserNameGetter } from 'hooks/user-user-name';
import { getUserLabel } from 'utils/get-user-label';

const useConstructDescription = () => {
  const translate = useTranslate();
  const { user } = useUser();
  const getName = useUserNameGetter();

  const constructTaskDescription = (data: Task) => data.taskDescription;
  const constructStatusUpdateDescription = (data: StatusUpdate) => {
    return translate(
      'projects.general.conversations.details.start.with_creator',
      {
        sameUser: user?.userId === data?.createdBy.userId,
        fromsame: (chunks) => (
          <span className="mention mention__self">{chunks}</span>
        ),
        fromother: (chunks) => <span className="mention">{chunks}</span>,
        user: getUserLabel(data.createdBy),
        date: <HumanisedDate timestamp={moment(data.createdAt)} />,
        type: 'status update',
      },
    );
  };
  const constructDocumentDescription = (data: DocumentContainer) => {
    const publicSection = data.document.sections.find(
      (section) => section.meta.sectionType === 'PUBLIC',
    );

    return translate(
      'projects.general.conversations.details.start.with_creator',
      {
        sameUser: user?.userId === publicSection?.createdBy,
        fromsame: (chunks) => (
          <span className="mention mention__self">{chunks}</span>
        ),
        fromother: (chunks) => <span className="mention">{chunks}</span>,
        user: getName(data.document.createdBy),
        date: <HumanisedDate timestamp={moment(publicSection?.createdAt)} />,
        type: 'document',
      },
    );
  };
  const constructTaskCreatedAt = (data: Task) => {
    return translate(
      'projects.general.conversations.details.start.with_creator',
      {
        sameUser: user?.userId === data?.createdBy,
        fromsame: (chunks) => (
          <span className="mention mention__self">{chunks}</span>
        ),
        fromother: (chunks) => <span className="mention">{chunks}</span>,
        user: getName(data.createdBy),
        date: <HumanisedDate timestamp={moment(data.createdAt)} />,
        type: 'task',
      },
    );
  };

  const constructSpaceTabDescription = (data: SpaceTab) => {
    return translate(
      'projects.general.conversations.details.start.with_creator',
      {
        sameUser: user?.userId === data.createdBy.userId,
        fromsame: (chunks) => (
          <span className="mention mention__self">{chunks}</span>
        ),
        fromother: (chunks) => <span className="mention">{chunks}</span>,
        user: getUserLabel(data.createdBy),
        date: <HumanisedDate timestamp={moment(data.createdAt)} />,
        type: 'document',
      },
    );
  };

  return {
    constructTaskDescription,
    constructStatusUpdateDescription,
    constructDocumentDescription,
    constructSpaceTabDescription,
    constructTaskCreatedAt,
  };
};

export const useFetchDescription = (
  projectId: number,
  activeConversation: ProjectGlobalConversation,
): {
  data?: {
    description?: ReactNode;
    createdAt?: ReactNode;
  };
  isFetching: boolean;
} => {
  const {
    constructDocumentDescription,
    constructStatusUpdateDescription,
    constructTaskDescription,
    constructSpaceTabDescription,
    constructTaskCreatedAt,
  } = useConstructDescription();
  const { data: description, isFetching } = useQuery(
    [
      'conversation-description',
      projectId,
      activeConversation.entityType,
      activeConversation.entityId,
    ],
    () => {
      if (activeConversation.displayEntityType === 'tasks') {
        return TaskAPI.getTask(projectId, activeConversation.entityId).then(
          (data) =>
            data.taskDescription
              ? { description: constructTaskDescription(data) }
              : { createdAt: constructTaskCreatedAt(data) },
        );
      }
      if (activeConversation.displayEntityType === 'status-updates') {
        return StatusUpdatesAPI.getStatusUpdateDetails([
          'status_update_id',
          activeConversation.statusUpdate.statusUpdateId,
        ]).then((data) => ({
          createdAt: constructStatusUpdateDescription(data),
        }));
      }
      if (activeConversation.entityType === ContextType.SPACE_TAB) {
        return SpaceTabAPI.getSpaceTab(activeConversation.entityId).then(
          (data) => ({ createdAt: constructSpaceTabDescription(data) }),
        );
      }
      if (activeConversation.entityType === ContextType.DOCUMENT) {
        return DocumentsAPI.getDocument(
          projectId,
          activeConversation.entityId,
        ).then((data) => ({ createdAt: constructDocumentDescription(data) }));
      }

      return Promise.reject();
    },
  );

  return { data: description, isFetching };
};
